import type { AnswerHead, Block, MatterContext, SectionPlanItem } from "../contract"

/** Two-digit tile index: 0 -> "01". */
export const idx = (i: number) => String(i + 1).padStart(2, "0")

const inr = new Intl.NumberFormat("en-IN")

/** "₹18.4 lakh" from rupees; small amounts in plain rupees. */
export function lakh(n: number): string {
  if (n >= 1e5) return `₹${parseFloat((n / 1e5).toPrecision(3))} lakh`
  return `₹${inr.format(Math.round(n))}`
}

/** "15 Oct 2026" from an ISO date; unparseable input is returned unchanged. */
export function shortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
}

/** True for null, undefined and blank strings: the UI hides those rows. */
export const blank = (v: unknown): v is null | undefined | "" =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "")

/** "Section 16(2)(c), section 16(4)" → "s.16(2)(c), s.16(4)"; "s.73" stays "s.73". */
export function sectionLabel(raw: string): string {
  return raw
    .split(/\s*[,;&]\s*|\s+and\s+/i)
    .map((part) => part.trim().replace(/^(?:s(?:ec(?:tion)?)?\.?\s*)+/i, "").trim())
    .filter(Boolean)
    .map((n) => `s.${n}`)
    .join(", ")
}

/** "2020-21" or "FY2020-21" → "FY 2020-21"; anything that is not a financial year is dropped. */
export function periodLabel(raw: string): string {
  const fys = [...raw.matchAll(/(20\d{2})\s*[-–/]\s*(\d{2,4})/g)].map((m) => `FY ${m[1]}-${m[2].slice(-2)}`)
  return [...new Set(fys)].join(", ")
}

/** The matter as chips, in a fixed order: State · FY · s.73 · form · amount · notice date. */
export function contextParts(ctx: (MatterContext & { form?: string | null }) | null | undefined): string[] {
  if (!ctx) return []
  const out: string[] = []
  if (!blank(ctx.state)) out.push(ctx.state.trim())
  if (!blank(ctx.period)) {
    const p = periodLabel(ctx.period)
    if (p) out.push(p)
  }
  if (!blank(ctx.section)) {
    const sec = sectionLabel(ctx.section)
    if (sec) out.push(sec)
  }
  if (!blank(ctx.form)) out.push(ctx.form.trim())
  if (typeof ctx.amount_inr === "number" && ctx.amount_inr > 0) out.push(lakh(ctx.amount_inr))
  if (!blank(ctx.notice_date) && /^\d{4}-\d{2}-\d{2}/.test(ctx.notice_date)) out.push(`notice ${shortDate(ctx.notice_date)}`)
  return out
}

/** "Assumed Finance Act..." → "Finance Act..."; the eyebrow already says it. */
export const assumption = (a: string) => a.replace(/^\s*assum(?:ed|ing|e)\b[:,]?\s*/i, "").replace(/\.\s*$/, "")

/** Split the short answer around the proof underline range (clamped); no range underlines it all. */
export function splitShort(head: AnswerHead): [string, string, string] {
  const s = head.short
  const u = head.underline
  if (!u) return ["", s, ""]
  const a = Math.max(0, Math.min(s.length, u.start))
  const b = Math.max(a, Math.min(s.length, u.end))
  return [s.slice(0, a), s.slice(a, b), s.slice(b)]
}

/** Takeaway for a section: the ask_back choice rewrites it locally. */
export function takeawayFor(
  item: SectionPlanItem,
  blocks: Block[] | undefined,
  choices: Record<string, string>,
  turn: string,
): string {
  if (!blocks) return item.takeaway
  let out = item.takeaway
  walkBlocks(blocks, (b, path) => {
    if (b.type !== "ask_back") return
    const v = choices[`${turn}:${item.id}:${path}`]
    const opt = v ? b.options.find((o) => o.value === v) : undefined
    if (opt && opt.takeaway) out = opt.takeaway
  })
  return out
}

/** Depth-first walk in reading order; paths match Blocks.tsx (`i`, nested `i.j`). */
export function walkBlocks(blocks: Block[], visit: (b: Block, path: string) => void, prefix = ""): void {
  blocks.forEach((b, i) => {
    const path = prefix ? `${prefix}.${i}` : String(i)
    visit(b, path)
    if (b.type === "disclosure") walkBlocks(b.blocks ?? [], visit, path)
  })
}

/** Number citations across a whole card, like the mockup: first mention of a source gets the next number. */
export function citeNumbers(blocks: Block[]): Record<string, number> {
  const out: Record<string, number> = {}
  let n = 0
  walkBlocks(blocks, (b) => {
    if (b.type !== "para") return
    for (const c of b.cites ?? []) if (!(c.source_id in out)) out[c.source_id] = ++n
  })
  return out
}
